export const PLATFORM_ROLE_PERMISSION_RESOURCES = [
  "tenants",
  "users",
  "subscriptions",
  "partners",
  "analytics",
  "monitoring",
  "settings",
] as const;

export type PlatformRolePermissionResource =
  (typeof PLATFORM_ROLE_PERMISSION_RESOURCES)[number];

export type PlatformRoleAccessLevel = "none" | "view" | "administrating";

const PLATFORM_ROLE_PERMISSION_RESOURCE_SET: ReadonlySet<string> = new Set(
  PLATFORM_ROLE_PERMISSION_RESOURCES,
);

const isPlatformRolePermissionResource = (
  value: string,
): value is PlatformRolePermissionResource =>
  PLATFORM_ROLE_PERMISSION_RESOURCE_SET.has(value);

export function getPlatformRolePermissionResource(
  permissionKey: string,
): PlatformRolePermissionResource | null {
  const [scope, resource, action] = permissionKey.split(":");
  if (
    scope !== "platform" ||
    !isPlatformRolePermissionResource(resource) ||
    (action !== "read" && action !== "administrate")
  ) {
    return null;
  }

  return resource;
}

export function getPlatformRoleAccessLevel(
  permissionKeys: readonly string[],
  resource: PlatformRolePermissionResource,
): PlatformRoleAccessLevel {
  const permissions = new Set(permissionKeys);

  if (
    permissions.has("platform:*:*") ||
    permissions.has(`platform:${resource}:*`) ||
    permissions.has(`platform:${resource}:administrate`)
  ) {
    return "administrating";
  }

  if (permissions.has(`platform:${resource}:read`)) {
    return "view";
  }

  return "none";
}

export function setPlatformRoleAccessLevel(
  permissionKeys: readonly string[],
  resource: PlatformRolePermissionResource,
  level: PlatformRoleAccessLevel,
): string[] {
  const readKey = `platform:${resource}:read`;
  const administrateKey = `platform:${resource}:administrate`;
  const nextKeys = permissionKeys.filter(
    (key) => key !== readKey && key !== administrateKey,
  );

  if (level === "view") {
    return [...nextKeys, readKey];
  }
  if (level === "administrating") {
    return [...nextKeys, administrateKey];
  }
  return nextKeys;
}

export function togglePlatformRolePermission(
  permissionKeys: readonly string[],
  permissionKey: string,
): string[] {
  const resource = getPlatformRolePermissionResource(permissionKey);
  if (!resource) {
    return permissionKeys.includes(permissionKey)
      ? permissionKeys.filter((key) => key !== permissionKey)
      : [...permissionKeys, permissionKey];
  }

  const clickedLevel: PlatformRoleAccessLevel = permissionKey.endsWith(
    ":administrate",
  )
    ? "administrating"
    : "view";
  const currentLevel = getPlatformRoleAccessLevel(permissionKeys, resource);

  return setPlatformRoleAccessLevel(
    permissionKeys,
    resource,
    currentLevel === clickedLevel ? "none" : clickedLevel,
  );
}

export function normalizePlatformRolePermissions(
  permissionKeys: readonly string[],
): string[] {
  return PLATFORM_ROLE_PERMISSION_RESOURCES.reduce<string[]>(
    (normalizedKeys, resource) => {
      const level = getPlatformRoleAccessLevel(permissionKeys, resource);
      return setPlatformRoleAccessLevel(normalizedKeys, resource, level);
    },
    permissionKeys.filter(
      (key) => getPlatformRolePermissionResource(key) === null,
    ),
  );
}

export function countPlatformPermissionSections(
  permissionKeys: readonly string[],
): number {
  return PLATFORM_ROLE_PERMISSION_RESOURCES.filter(
    (resource) =>
      getPlatformRoleAccessLevel(permissionKeys, resource) !== "none",
  ).length;
}
